import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  AuthShell,
  Field,
  focusFirstError,
  PasswordField,
  PRIMARY_BUTTON_CLASS,
} from "../components/auth-form.tsx";
import { ErrorMessage } from "../components/legacy/ui.tsx";
import { signUp } from "../lib/auth-client.ts";
import {
  collect,
  type Errors,
  parseAuthSearch,
  validateEmail,
  validatePassword,
  validateRequired,
} from "../lib/auth-validation.ts";

export const Route = createFileRoute("/sign-up")({
  component: SignUp,
  validateSearch: parseAuthSearch,
});

type FormField = "email" | "firstName" | "lastName" | "password";

/** Tab order, so focus lands on the first failure as it reads on screen. */
const ORDER: readonly FormField[] = ["email", "firstName", "lastName", "password"];

function SignUp() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Errors<FormField>>({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  // Organisation is absent on purpose: the API takes it as optional.
  const check = () =>
    collect<FormField>({
      email: validateEmail(email),
      firstName: validateRequired(firstName, "first name"),
      lastName: validateRequired(lastName, "last name"),
      password: validatePassword(password),
    });

  const revalidate = () => {
    if (submitted) setErrors(check());
  };

  const registerCustomer = async () => {
    setSubmitted(true);
    setFormError("");
    const found = check();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, ORDER);
      return;
    }

    setBusy(true);
    const result = await signUp.email({
      email: email.trim(),
      password,
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      organisation: organisation.trim() || undefined,
    } as Parameters<typeof signUp.email>[0]);
    setBusy(false);
    if (result.error) {
      setFormError(result.error.message ?? "Registration failed");
      return;
    }

    // Better Auth signs the new user in, so there is nothing to click
    // through: refresh the session the router guards on, then go. The await
    // matters - /account bounces to /sign-in on a cached anonymous config.
    await queryClient.invalidateQueries({ queryKey: ["config"] });
    if (redirect) navigate({ href: redirect });
    else navigate({ to: "/account" });
  };

  return (
    <AuthShell title="Create a new account">
      <form
        noValidate
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          registerCustomer();
        }}
      >
        {formError !== "" && (
          <ErrorMessage heading="We ran into a problem signing you up!" message={formError} />
        )}

        <Field
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          error={errors.email}
          onChange={(v) => {
            setEmail(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <Field
          label="First name"
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          error={errors.firstName}
          onChange={(v) => {
            setFirstName(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <Field
          label="Last name"
          name="lastName"
          autoComplete="family-name"
          value={lastName}
          error={errors.lastName}
          onChange={(v) => {
            setLastName(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <Field
          label="Organisation"
          name="organisation"
          autoComplete="organization"
          optional
          value={organisation}
          onChange={setOrganisation}
        />

        <PasswordField
          label="Password"
          name="password"
          autoComplete="new-password"
          showRule
          value={password}
          error={errors.password}
          onChange={(v) => {
            setPassword(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={busy}>
          {busy ? "Creating your account…" : "Sign Up"}
        </button>

        <p className="text-center text-sm text-gray-600">
          Or{" "}
          <Link
            to="/sign-in"
            search={true}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            login to your existing account
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
